/**
 * WebGL Voxel Renderer
 */

import type { Block } from "../types";
import { BlockRenderer } from "./base";
import { Mat4 } from "../math";

export class WebGLBlockRenderer extends BlockRenderer {
  private gl: WebGLRenderingContext;
  private program!: WebGLProgram;
  private buffers!: {
    position: WebGLBuffer;
    color: WebGLBuffer;
    normal: WebGLBuffer;
    index: WebGLBuffer;
    count: number;
  };
  protected blocks: Block[] = [];

  constructor(canvas: HTMLCanvasElement, blocks: Block[], modelScale: number) {
    super(blocks, modelScale);
    const gl = canvas.getContext("webgl", { alpha: true, antialias: false });
    if (!gl) throw new Error("WebGL not supported");
    this.gl = gl;

    this.initGL();
    this.setBlocks(blocks);
  }

  public setBlocks(blocks: Block[]): void {
    this.blocks = blocks;
    // ... Existing buffer management logic ...
    if (this.buffers && (this.buffers.position || this.buffers.color || this.buffers.normal || this.buffers.index)) {
      this.gl.deleteBuffer(this.buffers.position);
      this.gl.deleteBuffer(this.buffers.color);
      this.gl.deleteBuffer(this.buffers.normal);
      this.gl.deleteBuffer(this.buffers.index);
    }

    if (this.blocks && this.blocks.length > 0) {
      this.buffers = this.createBuffers();
    }
  }

  private createBuffers() {
    const gl = this.gl;
    const positions: number[] = [];
    const colors: number[] = [];
    const normals: number[] = [];
    const indices: number[] = [];
    let idx = 0;

    this.blocks!.forEach(block => {
      // Block properties
      const { center, width, height, depth, color } = block;
      const [cx, cy, cz] = center;

      // Convert to world coordinates
      // Half dimensions
      const hx = (width * this.modelScale) / 2;
      const hy = (height * this.modelScale) / 2;
      const hz = (depth * this.modelScale) / 2;

      const wx = cx * this.modelScale;
      const wy = cy * this.modelScale;
      const wz = cz * this.modelScale;

      // Add 6 faces (Front, Back, Top, Bottom, Right, Left)
      const addFace = (verts: number[], normal: number[]) => {
        positions.push(...verts);
        for (let i = 0; i < 4; i++) colors.push(...color);
        for (let i = 0; i < 4; i++) normals.push(...normal);
        // Triangle 1: 0, 1, 2. Triangle 2: 0, 2, 3.
        indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
        idx += 4;
      };

      // Front Face (+Z)
      addFace([
        wx - hx, wy - hy, wz + hz, // Bottom Left
        wx + hx, wy - hy, wz + hz, // Bottom Right
        wx + hx, wy + hy, wz + hz, // Top Right
        wx - hx, wy + hy, wz + hz  // Top Left
      ], [0, 0, 1]);

      // Back Face (-Z)
      addFace([
        wx + hx, wy - hy, wz - hz,
        wx - hx, wy - hy, wz - hz,
        wx - hx, wy + hy, wz - hz,
        wx + hx, wy + hy, wz - hz
      ], [0, 0, -1]);

      // Top Face (+Y)
      addFace([
        wx - hx, wy + hy, wz + hz,
        wx + hx, wy + hy, wz + hz,
        wx + hx, wy + hy, wz - hz,
        wx - hx, wy + hy, wz - hz
      ], [0, 1, 0]);

      // Bottom Face (-Y)
      addFace([
        wx - hx, wy - hy, wz - hz,
        wx + hx, wy - hy, wz - hz,
        wx + hx, wy - hy, wz + hz,
        wx - hx, wy - hy, wz + hz
      ], [0, -1, 0]);

      // Right Face (+X)
      addFace([
        wx + hx, wy - hy, wz + hz,
        wx + hx, wy - hy, wz - hz,
        wx + hx, wy + hy, wz - hz,
        wx + hx, wy + hy, wz + hz
      ], [1, 0, 0]);

      // Left Face (-X)
      addFace([
        wx - hx, wy - hy, wz - hz,
        wx - hx, wy - hy, wz + hz,
        wx - hx, wy + hy, wz + hz,
        wx - hx, wy + hy, wz - hz
      ], [-1, 0, 0]);
    });

    const pb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, pb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

    const cb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, cb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);

    const nb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, nb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);

    const ib = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array(indices),
      gl.STATIC_DRAW
    );

    return { position: pb, color: cb, normal: nb, index: ib, count: indices.length };
  }

  private initGL() {
    const gl = this.gl;
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);

    const vsSource = `
            attribute vec4 aPos;
            attribute vec4 aCol;
            attribute vec3 aNorm;
            uniform mat4 uMV;
            uniform mat4 uP;
            uniform mat4 uNormalMatrix;
            varying lowp vec4 vCol;
            varying mediump vec3 vNorm;
            void main() {
                gl_Position = uP * uMV * aPos;
                vCol = aCol;
                vNorm = mat3(uNormalMatrix) * aNorm;
            }
        `;
    const fsSource = `
            precision mediump float;
            varying lowp vec4 vCol;
            varying mediump vec3 vNorm;
            uniform vec3 uLightDir;
            void main() {
                vec3 normal = normalize(vNorm);
                vec3 lightDir = normalize(uLightDir);
                float ambient = 0.5;
                float diff = max(dot(normal, lightDir), 0.0);
                float light = ambient + diff;
                gl_FragColor = vec4(vCol.rgb * light, vCol.a);
            }
        `;

    const createShader = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        const info = gl.getShaderInfoLog(s);
        console.error('Shader compilation error:', info);
        gl.deleteShader(s);
        throw new Error('Shader compilation failed: ' + info);
      }
      return s;
    };

    const prog = gl.createProgram()!;
    gl.attachShader(prog, createShader(gl.VERTEX_SHADER, vsSource));
    gl.attachShader(prog, createShader(gl.FRAGMENT_SHADER, fsSource));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(prog);
      console.error('Program linking error:', info);
      throw new Error('Program linking failed: ' + info);
    }
    this.program = prog;
  }

  render(width: number, height: number, rotX: number, rotY: number, zoom = 1.0) {
    const gl = this.gl;
    gl.viewport(0, 0, width, height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.useProgram(this.program);

    const aspect = width / height;
    // Using perspective for WebGL (can be changed to ortho if desired)
    const proj = Mat4.perspective((45 * Math.PI) / 180, aspect, 0.1, 100.0);
    let mv = Mat4.identity();
    mv = Mat4.rotateX(mv, rotX);
    mv = Mat4.rotateY(mv, rotY);

    // Constant distance ensures fixed percentage of window height
    const distance = -6;
    mv = Mat4.translate(mv, 0, 0, distance / zoom);

    gl.uniformMatrix4fv(
      gl.getUniformLocation(this.program, "uP"),
      false,
      new Float32Array(proj)
    );
    gl.uniformMatrix4fv(
      gl.getUniformLocation(this.program, "uMV"),
      false,
      new Float32Array(mv)
    );

    // Calculate normal matrix: transpose of inverse of modelview matrix
    const normalMatrix = Mat4.transpose(Mat4.inverse(mv));
    gl.uniformMatrix4fv(
      gl.getUniformLocation(this.program, "uNormalMatrix"),
      false,
      new Float32Array(normalMatrix)
    );

    // Set light direction (top-right-front)
    const lightDir = [0.85, 0.8, 0.75];
    gl.uniform3fv(
      gl.getUniformLocation(this.program, "uLightDir"),
      new Float32Array(lightDir)
    );

    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.vertexAttribPointer(
      gl.getAttribLocation(this.program, "aPos"),
      3,
      gl.FLOAT,
      false,
      0,
      0
    );
    gl.enableVertexAttribArray(gl.getAttribLocation(this.program, "aPos"));

    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.color);
    gl.vertexAttribPointer(
      gl.getAttribLocation(this.program, "aCol"),
      4,
      gl.FLOAT,
      false,
      0,
      0
    );
    gl.enableVertexAttribArray(gl.getAttribLocation(this.program, "aCol"));

    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.normal);
    gl.vertexAttribPointer(
      gl.getAttribLocation(this.program, "aNorm"),
      3,
      gl.FLOAT,
      false,
      0,
      0
    );
    gl.enableVertexAttribArray(gl.getAttribLocation(this.program, "aNorm"));

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.buffers.index);
    gl.drawElements(gl.TRIANGLES, this.buffers.count, gl.UNSIGNED_SHORT, 0);
  }

  dispose() {
    // Clean up buffers
    if (this.buffers) {
      this.gl.deleteBuffer(this.buffers.position);
      this.gl.deleteBuffer(this.buffers.color);
      this.gl.deleteBuffer(this.buffers.normal);
      this.gl.deleteBuffer(this.buffers.index);
    }
  }
}
